import { renderToStaticMarkup } from 'react-dom/server'
import { cn } from '@/lib/utils'
import { REGION_BORDER } from '@/map/regionClasses'
import type { RegionColor } from '@/map/regionColors'

export type RegionLabelProps =
  | {
      /** A region's name, written on the photography: an area, a park. */
      kind: 'name'
      text: string
      /**
       * Lettering of the page's own, in place of white on the photography:
       * the proposal's plan sets names in its ink.
       */
      className?: string
    }
  | {
      /** A zone's number, on a badge edged in the zone's color. */
      kind: 'number'
      text: string
      color: RegionColor
      /** The zone being walked, drawn larger. */
      current?: boolean
      /** Another zone while one is walked, standing back as its outline does. */
      faded?: boolean
    }

/**
 * The words on a region, centered on the point they are placed at. A name
 * is white with a dark halo, because it sits on turf, roof and asphalt in
 * turn; a number is a small badge, since a lone digit in white is lost the
 * moment it crosses a sidewalk.
 */
export function RegionLabel(props: RegionLabelProps) {
  if (props.kind === 'name') {
    return (
      <span
        data-region-label=""
        className={cn(
          'absolute left-0 top-0 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap leading-none',
          props.className ??
            'text-[12px] font-semibold text-map-halo [text-shadow:0_0_3px_var(--color-map-ink),0_0_2px_var(--color-map-ink),0_1px_1px_var(--color-map-ink)]',
        )}
      >
        {props.text}
      </span>
    )
  }
  return (
    <span
      data-region-label=""
      className={cn(
        'absolute left-0 top-0 flex h-5 min-w-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center whitespace-nowrap rounded-full border-2 bg-map-marker-surface px-1 text-[11px] font-semibold leading-none text-map-ink shadow-sm',
        REGION_BORDER[props.color],
        props.current === true ? 'h-6 min-w-6 text-[12px]' : null,
        props.faded === true ? 'opacity-40' : null,
      )}
    >
      {props.text}
    </span>
  )
}

export function regionLabelHtml(props: RegionLabelProps): string {
  return renderToStaticMarkup(<RegionLabel {...props} />)
}
